/* eslint-disable camelcase*/
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { useGetActiveActionPlansQuery } from './activeActionPlansSlice';
import { selectCurrentEmpid } from '../../../pages/login/authSlice';
import './activeActionPlans.scss';
import { CoachingNextBestActionType } from '../../../utils/typeConstants';
import DeleteDraftCAP from '../deleteDraftCAP/DeleteDraftCAP';

const STEPS = {
    PREPARE: 0,
    ANALYZE: 1,
    COACH: 2,
    FOLLOW_UP: 3,
} as const;
interface ActiveActionPlansProps {
    scorecardVersionId: number;
    teamLeadId?: number;
    via?: string;
    setActionPlanId?: React.Dispatch<React.SetStateAction<number | null>>;
    setSessionId?: React.Dispatch<React.SetStateAction<number | null>>;
    setShowAcknowledge?: React.Dispatch<React.SetStateAction<boolean>>;
}
const ActiveActionPlans: React.FC<ActiveActionPlansProps> = ({ scorecardVersionId, teamLeadId, via, setActionPlanId, setSessionId, setShowAcknowledge }) => {
    const navigate = useNavigate();
    const loggedEmpNum = useSelector(selectCurrentEmpid);
    const [actionPlansData, setActionPlansData] = useState<any>(null);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);
    const [actionPlanToDelete, setActionPlanToDelete] = useState<number | null>(null);

    const { data: actionPlansDataSlice } = useGetActiveActionPlansQuery({
        employeeNumber: (via === 'managerCAP' && teamLeadId) ? teamLeadId : loggedEmpNum || 0,
        scorecardVersionId,
    },
        { skip: !loggedEmpNum || !scorecardVersionId });

    useEffect(() => {
        //filter out the draft records for manager view
        const filtered = via === 'managerCAP'
            ? actionPlansDataSlice?.data?.filter((action: any) => Number(action?.coaching_action_plan_status_id) !== 1)
            : actionPlansDataSlice?.data;
        setActionPlansData(filtered);
    }, [actionPlansDataSlice, via]);

    const formatDate = (dateStr: string): string => {
        if (dateStr !== null && dateStr !== '') {
            const date = new Date(dateStr);
            const day = date.getDate();
            const suffix = (d: number) => {
                if (d > 3 && d < 21) return 'th';
                switch (d % 10) {
                    case 1: return 'st';
                    case 2: return 'nd';
                    case 3: return 'rd';
                    default: return 'th';
                }
            };
            return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric', day: 'numeric' })
                .replace(/\d+,/, `${day}${suffix(day)},`);
        }
        return '';
    };
    const navigateToCoach = (actionDetails: any) => {
        sessionStorage.setItem('cap_nav_intent', 'true');
        navigate('/coachingActionPlan', {
            state: {
                activeStep: STEPS.COACH,
                isReviewSession: true,
                coachingActionPlanId: actionDetails?.coaching_action_plan_id,
                coachingActionPlanSessionId: actionDetails?.coaching_action_plan_session_id,
                employeeNumber: actionDetails?.employee_number,
                employeeName: actionDetails?.employee_name,
                scorecardVersionId,
                scorecardMetricId: actionDetails?.scorecard_metric_id,
                coachingNextBestActionId: actionDetails?.coaching_next_best_action_id,
                metricName: actionDetails?.metric_name,
                metricCurrentValue: actionDetails?.metric_current_value ?? null,
                metricTargetValue: actionDetails?.metric_target_value ?? null,
                metricTypeId: actionDetails?.metric_type_id,
                metricTypeName: actionDetails?.metric_type_name,
                metricDirectionId: actionDetails?.metric_direction_id,
                metricIndex: 0,
                metricStartDate: actionDetails?.start_date ?? null,
                metricEndDate: actionDetails?.end_date ?? null,

            },
        });
    };
    const navigateToFollowUp = (data: any) => {
        sessionStorage.setItem('cap_nav_intent', 'true');
        navigate('/coachingActionPlan', {
            state: {
                activeStep: STEPS.FOLLOW_UP,
                coachingActionPlanId: data.coaching_action_plan_id,
                employeeNumber: data.employee_number,
                employeeName: data.employee_name,
                scorecardVersionId,
                scorecardMetricId: data?.scorecard_metric_id,
                coachingNextBestActionId: data?.coaching_next_best_action_id,
                metricName: data?.metric_name,
                metricCurrentValue: data?.metric_current_value ?? null,
                metricTargetValue: data?.metric_target_value ?? null,
                metricTypeId: data?.metric_type_id,
                metricTypeName: data?.metric_type_name,
                metricDirectionId: data?.metric_direction_id,
                metricIndex: 0,
            },
        });
    };
    const navigateToAnalyze = (actionDetails: any) => {
        sessionStorage.setItem('cap_nav_intent', 'true');
        navigate('/coachingActionPlan', {
            state: {
                activeStep: STEPS.ANALYZE,
                coachingActionPlanId: actionDetails?.coaching_action_plan_id,
                coachingActionPlanSessionId: actionDetails?.coaching_action_plan_session_id,
                employeeNumber: actionDetails?.employee_number,
                employeeName: actionDetails?.employee_name,
                scorecardVersionId,
                scorecardMetricId: actionDetails?.scorecard_metric_id,
                coachingNextBestActionId: actionDetails?.coaching_next_best_action_id,
                metricName: actionDetails?.metric_name,
                metricCurrentValue: actionDetails?.metric_current_value ?? null,
                metricTargetValue: actionDetails?.metric_target_value ?? null,
                metricTypeId: actionDetails?.metric_type_id,
                metricTypeName: actionDetails?.metric_type_name,
                metricDirectionId: actionDetails?.metric_direction_id,
                metricIndex: 0,
                metricStartDate: actionDetails?.start_date ?? null,
                metricEndDate: actionDetails?.end_date ?? null,

            },
        });
    };
    const handleActionClick = (actionDetails: any) => {
        switch (actionDetails?.coaching_next_best_action_type_id) {
            case CoachingNextBestActionType.StartCoaching:
                navigateToAnalyze(actionDetails);
                break;
            case CoachingNextBestActionType.Recognize:
                navigateToFollowUp(actionDetails);
                break;
            case CoachingNextBestActionType.Review:
                if (actionDetails?.session_number >= 1) {
                    navigateToFollowUp(actionDetails);
                }
                else {
                    navigateToCoach(actionDetails);
                }
                break;
            case CoachingNextBestActionType.FollowUp:
                navigateToFollowUp(actionDetails);
                break;
            default:
                navigateToFollowUp(actionDetails);
        }
    };
    const getActionBadgeClass = (typeId: number) => {
        switch (typeId) {
            case CoachingNextBestActionType.StartCoaching: return 'start-coaching-btn';
            case CoachingNextBestActionType.Recognize: return 'recognize-btn';
            case CoachingNextBestActionType.FollowUp: return 'followup-btn';
            case CoachingNextBestActionType.Review: return 'review-btn';
            default: return 'review-btn';
        }
    };
    const getAcknowledgeLabel = (action: any): string => {
        if (Number(action.coaching_action_plan_status_id) === 1) return 'Draft';
        return action.acknowledged ? 'Acknowledged' : 'Not Acknowledged';
    };

    const getAcknowledgeClass = (action: any): string => {
        if (Number(action.coaching_action_plan_status_id) === 1) return 'draft';
        return action.acknowledged ? 'acknowledge' : 'not-acknowledge';
    };
    const handleViewActionPlan = (row: any) => {
        if (setActionPlanId) setActionPlanId(row.coaching_action_plan_id);
        if (setSessionId) setSessionId(row.coaching_action_plan_session_id);
        if (setShowAcknowledge) setShowAcknowledge(true);
    };
    const deleteDraft = (e: any, rowData: any) => {
        if (rowData.coaching_action_plan_id) {
            setActionPlanToDelete(rowData.coaching_action_plan_id);
            setShowConfirmationDialog(true);
        }
    };
    return (
        <div className="action-plans-container">

            <div className="action-plans-list">
                {actionPlansData?.map((action: any) => (
                    <div key={`${action.coaching_action_plan_id}-${action.coaching_action_plan_session_id}`}
                        className="action-plan-card">
                        {/* Avatar */}
                        <div className="avatar">
                            <span className="avatar-icon">{action.employee_name_shortcut}</span>
                        </div>

                        {/* Divider */}
                        <div className="vertical-divider" />

                        {/* Employee Info */}
                        <div className="employee-info">
                            <div className="employee-name">
                                <span className="employee-name">
                                    {action?.employee_name?.substring(0, action.employee_name.lastIndexOf('('))?.trim()}
                                </span>
                                <span className="employee-number"> ({action.employee_number})</span>
                            </div>
                        </div>

                        {/* Divider */}
                        <div className="vertical-divider" />

                        {/* Metric and Status */}
                        <div className="metric-info">
                            <div className="metric-name">{action.metric_name}</div>
                            <div className={`${getAcknowledgeClass(action)} ${actionPlanToDelete === action.coaching_action_plan_id && showConfirmationDialog ? 'pending-delete' : ''}`}>
                                {getAcknowledgeLabel(action)}
                                {Number(action.coaching_action_plan_status_id) === 1 && via !== 'managerCAP' && (
                                    <Button
                                        className={`deleteButton ${actionPlanToDelete === action.coaching_action_plan_id && showConfirmationDialog ? 'confirm-delete' : ''}`}
                                        type="button"
                                        icon="pi pi-trash"
                                        onClick={(e: any) => deleteDraft(e, action)}
                                    />
                                )}
                            </div>
                            {Number(action.coaching_action_plan_status_id) !== 1 && (
                            <div className={`status-detail`}>{formatDate(action.follow_up_date) ? `Follow Up on ${formatDate(action.follow_up_date)}` : 'Follow Up not scheduled'}</div>
                            )}
                        </div>

                        {/* Divider */}
                        <div className="vertical-divider" />

                        {/* Actions */}
                        {via === 'managerCAP' ? (
                            <div className="action-buttons">
                                <button className={`follow-up-btn followup-btn`} style={{ width: '100px' }} onClick={() => handleViewActionPlan(action)}>
                                    <span>View</span>
                                    <span style={{ marginLeft: '6px' }}>  |
                                        <i className="pi pi-arrow-circle-right follow-up-icon" /></span>
                                </button>
                            </div>
                        ) : (
                            <div className="action-buttons">
                                <button className={`follow-up-btn ${getActionBadgeClass(action.coaching_next_best_action_type_id)}`} onClick={() => handleActionClick(action)}>
                                    <span>{action.next_best_action_status_name}</span>
                                    <span style={{ marginLeft: '6px' }}>  |
                                        <i className="pi pi-arrow-circle-right follow-up-icon" /></span>
                                </button>
                            </div>
                        )}
                    </div>
                ))}
                {actionPlansData?.length === 0 && (
                    <div className="no-action-plans">
                        <p>No active action plans. Great job staying current!</p>
                    </div>
                )}
            </div>
            <DeleteDraftCAP
                coachingActionPlanId={actionPlanToDelete ?? 0}
                showDialog={showConfirmationDialog}
                setShowDialog={setShowConfirmationDialog}
            />
        </div>
    );
};

export default ActiveActionPlans;